//
// FieldData
//
// A plain FieldData object to hold properties

import { action, computed, makeObservable, observable } from 'mobx'

import { hashData, test_empty } from '../../utils/utils'
import { clearMetadataField, setMetadataField, updateInstanceWithData } from './utils'

export class FieldData {
    gid = null
    original = null
    name = ''
    type = ''
    options = new Map()
    is_localized = true
    is_required = false
    label = new Map()
    initial = new Map()
    _initial = new Map()
    unit = ''
    metadata = new Map()
    is_new = false
    is_working_copy = false

    _modifications = new Set()

    constructor(fielddata, rootstore) {
        makeObservable(this, {
            original: observable,
            name: observable,
            type: observable,
            options: observable,
            is_localized: observable,
            is_required: observable,
            label: observable,
            initial: observable,
            unit: observable,
            metadata: observable,
            renamed_from: computed,
            is_working_copy: observable,
            is_new: observable,
            is_committed: computed,
            is_multiline: computed,
            update: action.bound,
            setProperty: action.bound,
            setOption: action.bound,
            setValuesOption: action.bound,
            setMetadataField: action.bound,
            clearMetadataField: action.bound,
            commitIfModified: action.bound,
            commit: action.bound,
            revert: action.bound,
            saveAsNew: action.bound,
            delete: action.bound,
        })

        this._rootstore = rootstore
        this.update(fielddata)
    }

    newDataValue = recursion_check => {
        if (recursion_check === undefined) recursion_check = []
        // if (this.initial.size) {
        //     missing language.....
        //     return objectFrom(this.initial)
        // } else
        if (['imagelist', 'filelist', 'recordlist', 'classlist'].includes(this.type)) {
            return []
        } else if (['class'].includes(this.type)) {
            const class_gid = this.options.get('class')
            if (!class_gid) return null
            const _class = this._rootstore.data.classes.get(class_gid)
            if (!_class) return null
            let newitem = new Map()
            for (const field_gid of _class.fields) {
                const field = this._rootstore.data.fields.get(field_gid)
                if (field) {
                    if (recursion_check.includes(field_gid)) {
                        newitem.set(field.name, null)
                    } else {
                        newitem.set(
                            field.name,
                            field.newDataValue([...recursion_check, field_gid])
                        )
                    }
                } else {
                    console.log('ClassField field ERROR', field_gid)
                }
            }
            return newitem
        } else if (
            ['text', 'textline', 'textlist', 'code', 'barcode'].includes(this.type)
        ) {
            return ''
        }
        return null
    }

    update = (fielddata, overwrite_resets) => {
        // overwrite_resets for initial only
        updateInstanceWithData(this, fielddata, [
            'gid',
            'original',
            'name',
            'type',
            'options',
            'is_localized',
            'is_required',
            'label',
            'initial',
            'unit',
            'metadata',
            'is_new',
            'is_working_copy',
        ])
        if (
            ['textline', 'textlist'].includes(this.type) &&
            !this.options.has('values')
        ) {
            this.options.set('values', [])
        }
        if (!overwrite_resets) {
            this._initial = new Map(this.initial) // store db value
        }
    }

    get renamed_from() {
        if (!this.is_working_copy) return null
        if (this.is_new) return null
        const original = this._rootstore.data.fields.get(this.original)
        if (!original || original.name === this.name) return null
        return original.name
    }

    get is_committed() {
        return !this.is_working_copy
    }

    get is_multiline() {
        return ['text', 'code', 'recordlist', 'imagelist', 'filelist'].includes(
            this.type
        )
    }

    setProperty = (propertyname, value, optional_language) => {
        const previous_value = optional_language
            ? this[propertyname].get(optional_language)
            : this[propertyname]
        if (previous_value === value) return false
        if (test_empty(previous_value) && test_empty(value)) return false

        let fielddata = {}
        if (
            ['name', 'type', 'is_localized', 'is_required', 'unit'].includes(
                propertyname
            )
        ) {
            fielddata[propertyname] = value
        } else if (['options'].includes(propertyname)) {
            fielddata[propertyname] = Object.fromEntries(this[propertyname])
            fielddata[propertyname] = { ...fielddata[propertyname], ...value }
        } else if (['label'].includes(propertyname)) {
            // label is -always- localized
            fielddata[propertyname] = Object.fromEntries(this[propertyname])
            fielddata[propertyname][optional_language] = value
        } else if (['initial'].includes(propertyname)) {
            // initial localization depends on is_localized setting
            fielddata[propertyname] = Object.fromEntries(this[propertyname])
            fielddata[propertyname][optional_language] = value
            if (!this.is_localized) {
                for (const language of Object.keys(fielddata[propertyname])) {
                    fielddata[propertyname][language] = value
                }
            }
        }
        this.update(fielddata, true)
        this._modifications.add(propertyname)
        return true
    }

    setOption = (optionname, value, optional_language) => {
        const options = this['options']
        let option = options.get(optionname)

        const previous_value = optional_language
            ? option
                ? option[optional_language]
                : undefined
            : option
        if (previous_value === value) return false
        if (test_empty(previous_value) && test_empty(value)) return false

        if (optional_language) {
            if (!option) option = {}
            if (this['is_localized']) {
                option[optional_language] = value
            } else {
                for (var language of this._rootstore.project.languages) {
                    option[language] = value
                }
            }
        } else {
            option = value
        }
        let new_options = { ...Object.fromEntries(options) }
        new_options[optionname] = option

        this.update({ options: new_options }, true)
        this._modifications.add('options')
        return true
    }

    setValuesOption = values => {
        let new_options = { ...Object.fromEntries(this['options']) }
        const previous_values = 'values' in new_options ? new_options['values'] : []
        if (hashData(previous_values) === hashData(values)) return false
        if (test_empty(previous_values) && test_empty(values)) return false

        new_options['values'] = values
        this.update({ options: new_options }, true)
        this._modifications.add('options')
        return true
    }

    resetInitial = language => {
        this.initial.set(language, this._initial.get(language))
    }

    setMetadataField = (name, type, is_localized, value, optional_language) => {
        const metadata = this['metadata']
        const languages = this._rootstore.project.languages
        const [new_metadata, is_updated] = setMetadataField(
            metadata,
            languages,
            name,
            type,
            is_localized,
            value,
            optional_language
        )
        if (!is_updated) {
            return false
        }
        this.update({ metadata: new_metadata }, true)
        this._modifications.add('metadata')
        return true
    }

    clearMetadataField = name => {
        const metadata = this['metadata']
        const [new_metadata, is_updated] = clearMetadataField(metadata, name)
        if (!is_updated) {
            return false
        }
        this.update({ metadata: new_metadata }, true)
        this._modifications.add('metadata')
        return true
    }

    commitIfModified = () => {
        // send gid + modified fields to server backend
        if (!this._modifications.size) {
            return Promise.resolve(true)
        }

        let updateparams = { field: this.gid }
        for (const fieldname of this._modifications.keys()) {
            updateparams[fieldname] = this[fieldname]
        }
        // basically fire & forget updates
        return this._rootstore._fetch('/fields/modify', updateparams).then(result => {
            this._modifications = new Set()
            return result
        })
    }

    commit = () => {
        const is_new = this.is_new
        return this._rootstore
            ._fetch('/fields/commit', { field: this.gid })
            .then(result => {
                if (!is_new) {
                    // delete the 'modified' version
                    this._rootstore.data.fields.delete(this.gid)
                }
                return result
            })
    }
    revert = () => {
        return this._rootstore
            ._fetch('/fields/revert', { field: this.gid })
            .then(result => {
                // delete the 'modified' version
                this._rootstore.data.fields.delete(this.gid)
                return result
            })
    }
    saveAsNew = () => {
        return this._rootstore._fetch('/fields/save_as_new', {
            field: this.gid,
        })
    }
    copy = () => {
        return this._rootstore._fetch('/fields/copy', {
            field: this.gid,
        })
    }
    delete = () => {
        return this._rootstore
            ._fetch('/fields/delete', { field: this.gid })
            .then(result => {
                this._rootstore.data.fields.delete(this.gid)
                return result
            })
            .catch(error => {
                // in use - report to user?
                return false
            })
    }
}
